import { useAppStore } from 'App/store';
import isEmpty from 'lodash/isEmpty';
import toInteger from 'lodash/toInteger';
import { Wrapper } from 'Order/ReviewOrder/Styles';
import { Children, cloneElement, useMemo } from 'react';
import { Dropdown, OverlayTrigger, Popover } from "react-bootstrap";
import { Link, useLocation } from 'react-router-dom';
import Button from 'shared/components/common/Button';
import Redirect from 'shared/components/common/Redirect';
import policies from 'shared/configs/rbac/accessPolicies';
import { EntityType } from 'shared/constants/entityTypes';
import { ENABLED_MODULES, PORTALVIEW_PERMISSION } from 'shared/constants/fields';
import { useEntityDisplayName } from 'shared/hooks/misc/fields';
import useAccessControl from 'shared/hooks/misc/useAccessControl';
import usePermissionCheck from 'shared/hooks/misc/usePermissionCheck';
import useSourceParameters from 'shared/hooks/misc/useSourceParameters';
import { pluralize } from 'shared/utils/string';
import styled from 'styled-components';
import OutstandingBalance from './PortalMainMenu/OutstandingBalance';
import UserMenu from './PortalTopNav/UserMenu';

export const CREATE_ORDER_PATH = 'createorder';
const menu = [
  {
    entity: EntityType.ORDER_REQUEST,
    accessPolicy: {
      portalView: PORTALVIEW_PERMISSION.ORDER_REQUEST_CREATE,
    },
  },
  {
    entity: EntityType.ORDER,
  },
  {
    entity: EntityType.INVOICE,
    accessPolicy: {
      module: ENABLED_MODULES.BILLING,
    },
  },
  {
    entity: EntityType.QUOTATION,
    accessPolicy: {
      module: ENABLED_MODULES.BILLING,
    },
  },
  {
    entity: EntityType.SOURCE,
    accessPolicy: {
      module: ENABLED_MODULES.SOURCES,
    },
  },
];

const Menu = styled(Link)`
  color: ${(p) => p.theme.color.palette.text.headerText};

  & > button {
    height: fit-content;
		white-space: nowrap;

    &:hover {
      border-bottom: 2px solid ${(p) => p.theme.color.palette.text.headerText};
    }

    ${(p) => p.$isActive && `
      border-bottom: 2px solid ${p.theme.color.palette.text.headerText};
    `}
  }
`;

function MenuItem({ entity, accessPolicy = {} }) {
  const { pathname } = useLocation();
  const { appStateDispatch, appState: { delinquent } } = useAppStore();
  
  // Order Request Creation
  const isCreateOrder = entity === EntityType.ORDER_REQUEST;
  const [labelSingular, labelPlural] = useEntityDisplayName(
    isCreateOrder ? EntityType.ORDER : entity
  );
  const btnLabel = isCreateOrder ? `+ Create ${labelSingular}` : labelPlural;

  // Other menu items...
  const name = isCreateOrder ? CREATE_ORDER_PATH : pluralize(entity);
  const urlId = toInteger(pathname.split('/').filter(Boolean).at(-1));
  const activePath = pathname.split('/').filter(Boolean).at(1);
  const { module, portalView, ...policy } = accessPolicy;

  // Route access control
  const routePolicy = isEmpty(policy) ? policies.route[name] : policy;
  const hasSufficientRoutePrivilege = useAccessControl(routePolicy);
  const hasSufficientModuleOrPortalview = usePermissionCheck(
    module || portalView
  );

  // Source requires different treatment
  const isSourceEntity = entity === EntityType.SOURCE;
  const { isEnabled: isSourceEnabled } = useSourceParameters();

  function handlePreClick(targetPathname) {
    return (e) => {
      const nextPath = name;
      const isInCreateFlow = pathname.includes(CREATE_ORDER_PATH);
      const isEditingOrder = isInCreateFlow && !!urlId;
      const isExitingFlow = isEditingOrder && nextPath !== CREATE_ORDER_PATH;
      const isStayingInFlow = isEditingOrder && nextPath === CREATE_ORDER_PATH;

      if (isStayingInFlow || isExitingFlow) e.preventDefault();
      appStateDispatch({
        type: 'RESET_CREATE_ORDER',
        payload: targetPathname,
      });
    };
  }

  const menuBtn = (
    <DelinquentOverlayTrigger isCreateOrder={isCreateOrder} key={name}>
      <Menu
        to={`/portal/${name}`}
        className="me-3 text-decoration-none"
        onClick={handlePreClick(`/portal/${name}`)}
        $isActive={name === activePath}
      >
        <Button
          type="headerText"
          className="m-2 mx-4 mb-0 p-0 pb-2 fs-7 rounded-0"
          disabled={isCreateOrder && delinquent}
          bgColor={isCreateOrder && delinquent ? 'danger' : 'transparent'}
        >
          {btnLabel}
        </Button>
      </Menu>
    </DelinquentOverlayTrigger>
  );

  if (isSourceEntity && !isSourceEnabled) return null;
  return !hasSufficientModuleOrPortalview || !hasSufficientRoutePrivilege
    ? null
    : menuBtn;
}

export default function MainMenu() {
  const menuItems = useMemo(() => menu, []);
  return (
    <nav className="d-flex ms-auto">
      {menuItems.map(MenuItem)}
      <OutstandingBalance />
      <UserMenu />
    </nav>
  );
}

export const DelinquentOverlayTrigger = ({ children, isCreateOrder }) => {
  const { appState: { delinquent } } = useAppStore();

  const popoverClickFocus = (
    <Popover>
      <Wrapper>
        Your account's ability to make modifications has been disabled. Contact your lab for more information.
      </Wrapper>
    </Popover>
  );

  const modifyChild = (child) => {
    if (child) {
      const newProps = { disabled: true, style: { pointerEvents: "auto", cursor: "not-allowed" } };

      if (child.props && child.props.onClick) {
      newProps.onClick = (e) => {
        e.preventDefault();
      };
      }

      if (child.props && child.props.className) {
      newProps.className = child.props.className
        .split(' ')
        .filter((className) => className !== 'bg-success' && className !== 'border-success')
        .join(' ');
      }

      if ([Button, Redirect, Dropdown.Item, "img", Link, 'a', Menu].includes(child.type)) {
      return cloneElement(child, newProps);
      }

      if (child.props && child.props.children) {
      return cloneElement(child, {
        children: Children.map(child.props.children, modifyChild),
      });
      }
    }
   

    return child;
  };

  const modifiedChildren = modifyChild(children);

  return (
    <OverlayTrigger trigger={["focus", "click", "hover"]} placement="bottom" overlay={(delinquent && isCreateOrder) ? popoverClickFocus : <></>}>
      <>
        {(delinquent && isCreateOrder) ? modifiedChildren : children}
      </>
    </OverlayTrigger>
  );
};
